"use client";

import { useEffect, useState } from "react";
import {
  Lock,
  Wallet,
  Smartphone,
  ZapIcon,
  CreditCard,
  Globe,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// BrandingPreview — high-fidelity mock of the actual pay.cashtap.cash
// checkout page. Used inside the Developers > Branding panel so the
// merchant can see exactly what buyers will see, in real time, as they
// edit their colours, logo, name, and payment-method allowlist.
//
// Layout mirrors the live hosted checkout: brand header → order summary
// (dummy product line items) → email field → payment method tiles → big
// branded CTA → trust footer. Everything that's editable on the form
// flows straight into here, including the logo URL (fetched live with
// graceful fallback to a brand-coloured initial tile if the image fails
// to load).
// ──────────────────────────────────────────────────────────────────────────

// Dummy line items used in the branding preview. We include placeholder
// image URLs so the merchant can see how product thumbnails will render
// on the live checkout. Static, public-domain Unsplash images keep the
// preview deterministic across deploys (no third-party API calls).
const DUMMY_LINE_ITEMS = [
  {
    name: "Premium Plan",
    description: "Monthly subscription",
    quantity: 1,
    unit: 49.0,
    image_url:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=120&h=120&fit=crop",
  },
  {
    name: "Setup Fee",
    description: "One-time onboarding",
    quantity: 1,
    unit: 1.0,
    image_url: null,
  },
];
const DUMMY_BUYER_EMAIL = "buyer@example.com";

const METHOD_META: Record<
  string,
  { label: string; icon: any; sub: string }
> = {
  wallet: { label: "Wallet Transfer", icon: Wallet, sub: "Pay in USD from any wallet" },
  cashapp: { label: "CashApp Pay", icon: Smartphone, sub: "Pay from CashApp balance" },
  coinbase: { label: "Coinbase Onramp", icon: CreditCard, sub: "Card or bank via Coinbase" },
  peer: { label: "Peer Extension", icon: Globe, sub: "Browser wallet" },
  "lightning-btc": { label: "Lightning BTC", icon: ZapIcon, sub: "Sats payment" },
  "card-bank": { label: "Card / Bank", icon: CreditCard, sub: "Pay with card or ACH" },
};

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function BrandingPreview({
  displayName,
  logoUrl,
  brandColor,
  accentColor,
  footerText,
  methods,
}: {
  displayName: string;
  logoUrl: string;
  brandColor: string;
  accentColor: string;
  footerText: string;
  methods: string[];
}) {
  const [logoOk, setLogoOk] = useState<boolean | null>(null);

  // Probe the logo URL on every change and fall back to a brand-coloured
  // initial tile if the image can't load (CORS, 404, malformed URL).
  useEffect(() => {
    if (!logoUrl) {
      setLogoOk(null);
      return;
    }
    setLogoOk(null);
    const img = new Image();
    img.onload = () => setLogoOk(true);
    img.onerror = () => setLogoOk(false);
    img.src = logoUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [logoUrl]);

  const initial = (displayName?.[0] || "?").toUpperCase();
  const showLogo = logoUrl && logoOk === true;

  const subtotal = DUMMY_LINE_ITEMS.reduce((s, it) => s + it.quantity * it.unit, 0);
  const total = subtotal; // No tax/discount in v1
  const selectedMethods = (methods && methods.length > 0 ? methods : ["wallet"]).filter(
    (m) => METHOD_META[m],
  );
  const primaryMethod = selectedMethods[0] || "wallet";

  return (
    <div className="rounded-2xl border border-gray-800 bg-[#080b10] overflow-hidden shadow-2xl shadow-black/40">
      {/* Faux browser chrome — sells the "this is a real page" framing */}
      <div className="px-3 py-2 border-b border-gray-800/80 bg-[#0a0d12] flex items-center gap-2">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/40" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500/40" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
        </div>
        <div className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1 rounded-md bg-black/40 border border-gray-800 text-[10px] text-gray-500 font-mono">
          <Lock className="w-2.5 h-2.5" />
          pay.cashtap.cash/c/cs_live_…
        </div>
      </div>

      {/* Page surface, tinted by brand color */}
      <div
        className="p-4"
        style={{
          background: `
            radial-gradient(140% 100% at 50% 0%, ${brandColor}1A, transparent 60%),
            radial-gradient(80% 60% at 100% 100%, ${accentColor}10, transparent 70%),
            #0a0d12
          `,
        }}
      >
        <div className="rounded-2xl bg-gradient-to-b from-[#0F1419]/95 to-[#0B0F16]/95 border border-gray-800/80 overflow-hidden">
          {/* ── Brand header ──────────────────────────────────────── */}
          <div
            className="px-4 py-3 border-b border-gray-800/80 flex items-center gap-3"
            style={{
              background: `linear-gradient(90deg, ${brandColor}14, transparent 70%)`,
            }}
          >
            {showLogo ? (
              <img
                src={logoUrl}
                alt={displayName}
                className="w-9 h-9 rounded-xl object-contain bg-white p-0.5 border border-white/10 shrink-0"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${brandColor}, ${accentColor})`,
                  color: "#0f1419",
                  boxShadow: `0 4px 12px -3px ${brandColor}66`,
                }}
              >
                {initial}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-white text-[13px] font-bold leading-tight truncate">
                {displayName || "Your business"}
              </p>
              <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5">
                <Lock className="w-2.5 h-2.5" />
                Secure checkout
              </p>
            </div>
            <span
              className="text-[9px] font-bold tracking-wider uppercase px-2 py-1 rounded-md"
              style={{ background: `${brandColor}22`, color: brandColor }}
            >
              Live
            </span>
          </div>

          {/* ── Order summary ────────────────────────────────────── */}
          <div className="px-4 py-3 border-b border-gray-800/80">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-gray-500">
                Order summary
              </p>
              <ChevronDown className="w-3 h-3 text-gray-600" />
            </div>
            <div className="space-y-2">
              {DUMMY_LINE_ITEMS.map((item, i) => {
                const accent = i === 0 ? brandColor : accentColor;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="relative w-10 h-10 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden"
                      style={{
                        background: item.image_url ? "#0a0d12" : `${accent}15`,
                        color: accent,
                        border: `1px solid ${accent}25`,
                      }}
                    >
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span>×{item.quantity}</span>
                      )}
                      {item.image_url && item.quantity > 1 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center tabular-nums shadow"
                          style={{ background: accent, color: "#0f1419" }}
                        >
                          ×{item.quantity}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-[12px] font-semibold leading-tight truncate">
                        {item.name}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate mt-0.5">
                        {item.description}
                      </p>
                    </div>
                    <p className="text-white text-[12px] font-semibold tabular-nums shrink-0">
                      ${fmt(item.quantity * item.unit)}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-800/60 space-y-1">
              <Row label="Subtotal" value={`$${fmt(subtotal)}`} />
              <Row
                label="Total"
                value={`$${fmt(total)} USD`}
                emphasis
                brandColor={brandColor}
              />
            </div>
          </div>

          {/* ── Email field mock ─────────────────────────────────── */}
          <div className="px-4 py-3 border-b border-gray-800/80">
            <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-gray-500 mb-1.5">
              Email
            </p>
            <div
              className="px-2.5 py-2 rounded-lg bg-black/30 border text-[11px] text-gray-500"
              style={{ borderColor: `${brandColor}33` }}
            >
              {DUMMY_BUYER_EMAIL}
            </div>
          </div>

          {/* ── Payment method tiles ─────────────────────────────── */}
          <div className="px-4 py-3 border-b border-gray-800/80">
            <p className="text-[10px] font-bold tracking-[0.16em] uppercase text-gray-500 mb-2">
              Payment method
            </p>
            <div className="space-y-1.5">
              {selectedMethods.slice(0, 4).map((id) => {
                const meta = METHOD_META[id];
                const Icon = meta.icon;
                const selected = id === primaryMethod;
                return (
                  <div
                    key={id}
                    className="flex items-center gap-2.5 p-2 rounded-lg border transition-colors"
                    style={{
                      background: selected ? `${brandColor}10` : "transparent",
                      borderColor: selected ? `${brandColor}55` : "rgba(75,85,99,0.35)",
                    }}
                  >
                    <span
                      className="w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0"
                      style={{
                        borderColor: selected ? brandColor : "rgba(107,114,128,0.5)",
                      }}
                    >
                      {selected && (
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: brandColor }}
                        />
                      )}
                    </span>
                    <span
                      className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                      style={{
                        background: `${brandColor}18`,
                        color: brandColor,
                      }}
                    >
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-[11px] font-semibold leading-tight">
                        {meta.label}
                      </p>
                      <p className="text-[9px] text-gray-500 leading-tight mt-0.5">
                        {meta.sub}
                      </p>
                    </div>
                  </div>
                );
              })}
              {selectedMethods.length > 4 && (
                <p className="text-[10px] text-gray-600 text-center pt-0.5">
                  +{selectedMethods.length - 4} more
                </p>
              )}
            </div>
          </div>

          {/* ── Pay CTA ──────────────────────────────────────────── */}
          <div className="px-4 py-3">
            <button
              type="button"
              className="w-full flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[12px] font-bold tracking-tight transition-transform"
              style={{
                background: `linear-gradient(135deg, ${brandColor}, ${accentColor})`,
                color: "#0f1419",
                boxShadow: `0 6px 18px -6px ${brandColor}80, inset 0 1px 0 rgba(255,255,255,0.15)`,
              }}
            >
              Pay ${fmt(total)} USD
              <ArrowRight className="w-3 h-3" strokeWidth={3} />
            </button>
            <div className="mt-2.5 flex items-center justify-center gap-1.5 text-[9px] text-gray-500">
              <ShieldCheck className="w-2.5 h-2.5" style={{ color: brandColor }} />
              <span>
                Payments secured by{" "}
                <span className="text-gray-400 font-semibold">CashTap</span>
              </span>
            </div>
            {footerText && (
              <p className="mt-1.5 text-[9px] text-gray-600 text-center leading-snug truncate">
                {footerText}
              </p>
            )}
          </div>
        </div>

        {logoUrl && logoOk === false && (
          <p className="mt-2 text-[10px] text-amber-400 text-center">
            Logo URL didn't load. Using the brand-color tile as a fallback.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  emphasis,
  brandColor,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  brandColor?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`text-[11px] ${
          emphasis ? "text-white font-bold tracking-tight" : "text-gray-500"
        }`}
      >
        {label}
      </span>
      <span
        className={`text-[11px] tabular-nums ${
          emphasis ? "font-bold" : "text-gray-400 font-medium"
        }`}
        style={emphasis ? { color: brandColor } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
